import React, { useEffect, useState } from "react";
import {
  Breadcrumb,
  BreadcrumbGroup,
  Button,
  Column,
  Icon,
  Input,
  ProgressTracker,
  ProgressTrackerStep,
  Row,
  Select,
  SelectOption,
  Text,
} from "@amzn/meridian";
import plusKnockoutTokens from "@amzn/meridian-tokens/base/icon/plus-knockout";
import { useNavigate } from "react-router-dom";
import {
  setCreateInspectionStep,
  updateInspectionDetails,
} from "src/store/templateCreationSlice";
import { useAppDispatch, useAppSelector } from "src/store/store";
import AddQuestionFormCard from "src/components/UI/addQuestionFormCard";
import { ConfigureInspectionStep, Question } from "src/data/types";
import { createToast } from "src/store/layoutSlice";
import { CREATE_INSPECTION_SUCCESS_TOAST } from "src/constants/toasts";
import { sampleQuestion } from "src/data/mockData";
import { ADD_DETAILS, ADD_QUESTIONS } from "src/constants/constants";

function CreateInspectionTemplate() {
  const [formToDisplay, setFormToDisplay] =
    useState<ConfigureInspectionStep>(ADD_DETAILS);
  const [questionForms, setQuestionForms] = useState<Question[]>([
    sampleQuestion,
  ]);
  const { createInspectionSteps, inspectionTemplateDetail } = useAppSelector(
    (state) => state.templateCreation
  );
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isInspectionDetailsFormFilled = Object.values(
    inspectionTemplateDetail
  ).every((value) => value !== "");
  const createInspectionBreadcrumbs = () => {
    return (
      <BreadcrumbGroup>
        <Breadcrumb onClick={() => navigate("/admin")}>Dashboard</Breadcrumb>
        <Breadcrumb>Create Inspection</Breadcrumb>
      </BreadcrumbGroup>
    );
  };

  useEffect(() => {
    const isInspectionDetailsFormFilled = Object.values(
      inspectionTemplateDetail
    ).every((value) => value !== "");
    if (isInspectionDetailsFormFilled) {
      dispatch(
        setCreateInspectionStep({
          step: "inspectionDetails",
          currentStep: "past",
        })
      );
      dispatch(
        setCreateInspectionStep({
          step: "configureQuestions",
          currentStep: "present",
        })
      );
    } else {
      dispatch(
        setCreateInspectionStep({
          step: "inspectionDetails",
          currentStep: "present",
        })
      );
      dispatch(
        setCreateInspectionStep({
          step: "configureQuestions",
          currentStep: "future",
        })
      );
    }
  }, [inspectionTemplateDetail]);
  const handleQuestionFormChange = (
    id: string,
    key: keyof Question,
    value: string
  ) => {
    setQuestionForms((prev) =>
      prev.map((question) =>
        question.questionId === id ? { ...question, [key]: value } : question
      )
    );
  };
  const deleteForm = (id: string) => {
    setQuestionForms((prev) => prev.filter((form) => form.questionId !== id));
  };
  return (
    <Column>
      <Row spacingInset={"none none 500"}>{createInspectionBreadcrumbs()}</Row>
      <Row alignmentVertical="top" spacing={"600"} widths={["fit", "fill"]}>
        <Column>
          <ProgressTracker>
            <ProgressTrackerStep
              type={createInspectionSteps.inspectionDetails}
              label="Inspection Details"
              showLabel={true}
            />
            <ProgressTrackerStep
              type={createInspectionSteps.configureQuestions}
              label="Configure Questions"
              showLabel={true}
            />
          </ProgressTracker>
        </Column>
        {formToDisplay === ADD_DETAILS && (
          <Column spacingInset={"none none none 600"} maxWidth={1000}>
            <Row alignmentHorizontal="justify" alignmentVertical="top">
              <Text
                fontFamily="bookerly"
                type="d50"
                className="remove-top-padding"
              >
                Create Inspection
              </Text>
              <Button
                disabled={!isInspectionDetailsFormFilled}
                onClick={() => setFormToDisplay(ADD_QUESTIONS)}
              >
                Save & Exit
              </Button>
            </Row>
            <Row>
              <Text>Inspection Details</Text>
            </Row>
            <Row widths={"fill"}>
              <Input
                label="Name"
                value={inspectionTemplateDetail.name}
                size="xlarge"
                onChange={(value) => {
                  dispatch(
                    updateInspectionDetails({
                      label: "name",
                      value: value,
                    })
                  );
                }}
              />
              <Select
                value={inspectionTemplateDetail.inspectionType}
                onChange={(value) =>
                  dispatch(
                    updateInspectionDetails({
                      label: "inspectionType",
                      value: value,
                    })
                  )
                }
                size="xlarge"
                label="Inspection Type"
              >
                <SelectOption
                  value={"Inspection Type 1"}
                  label="Inspection Type 1"
                />
                <SelectOption
                  value={"Inspection Type 2"}
                  label="Inspection Type 2"
                />
                <SelectOption
                  value={"Inspection Type 3"}
                  label="Inspection Type 3"
                />
              </Select>
            </Row>
            <Row widths={"fill"}>
              <Select
                value={inspectionTemplateDetail.location}
                onChange={(value) =>
                  dispatch(
                    updateInspectionDetails({ label: "location", value: value })
                  )
                }
                size="xlarge"
                label="Location"
              >
                <SelectOption value={"Location 1"} label="Location 1" />
                <SelectOption value={"Location 2"} label="Location 2" />
                <SelectOption value={"Location 3"} label="Location 3" />
              </Select>
              <Select
                value={inspectionTemplateDetail.frequency}
                onChange={(value) =>
                  dispatch(
                    updateInspectionDetails({
                      label: "frequency",
                      value: value,
                    })
                  )
                }
                size="xlarge"
                label="Frequency"
              >
                <SelectOption value={"Frequency 1"} label="Frequency 1" />
                <SelectOption value={"Frequency 2"} label="Frequency 2" />
                <SelectOption value={"Frequency 3"} label="Frequency 3" />
              </Select>
            </Row>
            <Row widths={"50%"}>
              <Select
                value={inspectionTemplateDetail.mandate}
                onChange={(value) =>
                  dispatch(
                    updateInspectionDetails({ label: "mandate", value: value })
                  )
                }
                size="xlarge"
                label="Mandate"
              >
                <SelectOption value={"Mandate 1"} label="Mandate 1" />
                <SelectOption value={"Mandate 2"} label="Mandate 2" />
                <SelectOption value={"Mandate 3"} label="Mandate 3" />
              </Select>
            </Row>
          </Column>
        )}
        {formToDisplay === ADD_QUESTIONS && (
          <Column>
            <Column spacingInset={"none none none 600"} maxWidth={1000}>
              <Row alignmentHorizontal="justify" alignmentVertical="top">
                <Text
                  fontFamily="bookerly"
                  type="d50"
                  className="remove-top-padding"
                >
                  Create Inspection
                </Text>
                <Button
                  onClick={() => {
                    dispatch(createToast(CREATE_INSPECTION_SUCCESS_TOAST));
                    navigate("/admin");
                  }}
                  disabled={questionForms.length < 1}
                >
                  Publish
                </Button>
              </Row>
              <Row>
                <Text type="h300">Inspection Questions</Text>
              </Row>
              <Column spacing={"600"}>
                {questionForms.map((form, index) => (
                  <AddQuestionFormCard
                    key={form.questionId}
                    form={form}
                    deleteForm={deleteForm}
                    handleQuestionFormChange={handleQuestionFormChange}
                  />
                ))}
              </Column>
            </Column>

            <Row spacingInset={"none 600"}>
              <Button
                onClick={() => {
                  setQuestionForms((prev) => [
                    ...prev,
                    {
                      questionId: String(Math.random()),
                      questionText: "",
                      instruction: "",
                      answerType: "",
                      examples: [],
                    },
                  ]);
                }}
                type="tertiary"
              >
                <Icon tokens={plusKnockoutTokens} />
                Add Questions
              </Button>
            </Row>
          </Column>
        )}
      </Row>
    </Column>
  );
}

export default CreateInspectionTemplate;
